
import { Directive, Input, OnDestroy } from "@angular/core";
import { BooleanAttribute } from "@hex/angular/decorators";
import { isEmpty } from "lodash";
import { ReplaySubject ,  Subscription } from "rxjs";
import { filter, first } from "rxjs/operators";
import { GenericActions } from "../../redux/generic-actions";
import { ImmutableRedux } from "../../redux/immutable-redux.service";
import { ReduxRequestsMap } from "../../redux/redux-requests-map.service";
import { DataListActions } from "./actions.types";

@Directive({
    selector: "[common-list-action-creators]",
    exportAs: "CommonListActions",
})
export class CommonListActionsDirective implements OnDestroy {
    @Input() public stateProp: string;
    @Input() public rootProp: string;
    @Input() public isExpandable: boolean;
    @Input() public trackByProp = "id";
    @Input() @BooleanAttribute() public keepStateAliveOnDestroy = false;
    public listRequest$: Subscription;
    public isInitialized$ = new ReplaySubject();

    constructor(
        private reduxRequests: ReduxRequestsMap,
        private ngRedux: ImmutableRedux<any>,
    ) { }

    public ngOnDestroy() {
        if (this.listRequest$) { this.listRequest$.unsubscribe(); }
    }

    public initialize() {
        if (this.getListOnInit) {
            this.getList();
        }
        this.isInitialized$.next(true);
    }

    public get getListOnInit() {
        if (this.state.listData.initWhenReady) {
            if (this.keepStateAliveOnDestroy && isEmpty(this.ngRedux.getState(this.rootProp, this.stateProp).listData.results)) {
                return true;
            } else if (!this.keepStateAliveOnDestroy) {
                return true;
            } else {
                return false;
            }
        } else {
            return false;
        }
    }

    public getList(isNewPage?: boolean): void {
        this.isInitialized$.pipe(filter((state) => !!state), first()).subscribe(() => {
            const params = this.state.listData.params;
            if (this.listRequest$) { this.listRequest$.unsubscribe(); }
            this.listRequest$ = this.reduxRequests.within(this.stateProp).getList(params)
                .map((body) => body.data)
                .subscribe((payload) => {
                    if (isNewPage) {
                        this.dispatch(DataListActions.LIST_PAGINATED, payload);
                    } else {
                        this.dispatch(DataListActions.LIST_LOADED, payload);
                    }
                    const limit = this.state.listData.params.limit;
                    if (limit === undefined || payload.length < limit) {
                        this.dispatch(DataListActions.LIST_EXHAUSTED);
                    }
                });
        });
    }

    public getDetail(id: string): void {
        this.reduxRequests.within(this.stateProp).getDetail({ id })
            .map((body) => body.data)
            .subscribe((payload) => {
                this.dispatch(DataListActions.DETAIL_LOADED, payload);
            });
    }

    public sortByColumn(col: any, index: number, event): void {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        if (!col.isSortable) {
            return;
        }

        const field = col.sortKey;
        this.dispatch(DataListActions.UPDATE_COLUMN_SORT_STATE, index);
        const colSortState = this.getColumnSortState(index);
        if (colSortState && colSortState.isSorted) {
            let direction: string | undefined;
            if (colSortState.sortDirection > 0) {
                direction = "ASC";
            } else if (colSortState.sortDirection < 0) {
                direction = "DESC";
            } else {
                direction = undefined;
            }

            const params: any = { sort: { field, direction } };
            this.askForParametrizedList(params);
        } else {
            this.clearSort();
        }
    }

    public clearSort(fields?): void {
        fields = fields || ["sort"];
        this.removeParams(fields);
        this.getList();
    }

    public updateColumnSortState(colIndex: number): void {
        this.dispatch(DataListActions.UPDATE_COLUMN_SORT_STATE, colIndex);
    }

    public search(searchText, argName): void {
        this.askForParametrizedList({ [argName]: searchText });
    }

    public resetList(): void {
        this.dispatch(GenericActions.COMPONENT_REMOVED);
    }

    public paginate(): void {
        const isNewPage = true;
        this.getList(isNewPage);
    }

    public filter(filterBy: string, filterValue: boolean): void {
        const params = { [filterBy]: filterValue };
        this.askForParametrizedList(params);
    }

    public clearFilters(): void {
        const payload = ["filterBy", "filterValue"];
        this.removeParams(payload);
        this.getList();
    }

    public listExhausted(): void {
        this.dispatch(DataListActions.LIST_EXHAUSTED);
    }

    public resetPagination(): void {
        this.dispatch(DataListActions.PARAMS_OFFSET_RESET);
    }

    public removeParams(payload): void {
        this.resetPagination();
        this.dispatch(DataListActions.PARAMS_REMOVED, payload);
    }

    public selectRow(row, index, e?): void {
        if (!!e) {
            e.stopPropagation();
        }
        if (!!this.isExpandable) {
            this.ngRedux.dispatchFactory(GenericActions.DISPLAY_TOGGLED, this.rootProp, this.stateProp, index);
        } else {
            const id = row.id;
            this.dispatch(DataListActions.ROW_SELECTED, id);
        }
    }

    private askForParametrizedList(params): void {
        this.resetPagination();
        this.dispatch(DataListActions.PARAMS_UPDATED, params);
        this.getList();
        this.isInitialized$.next(true);
    }

    public get state(): any {
        return this.ngRedux.getState(this.rootProp, this.stateProp);
    }

    private dispatch(actionType, payload?): void {
        this.ngRedux.dispatchFactory(actionType, this.rootProp, this.stateProp, payload);
    }

    public getColumnSortState(index) {
        return this.state.listData["columns"][index];
    }

    public trackByFn(index: number, item): number {
        return this.trackByProp ? item[this.trackByProp] : index;
    }
}
